import { Analytics } from '@cobliteam/analytics/src/Analytics';
import type { LocalUserPreferences } from '@cobliteam/api-lib';
import { useActivationTasks } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n/src/hooks/useTranslation';
import type { Permission } from '@cobliteam/permissions';
import { usePermissions } from '@cobliteam/permissions';
import { Button, Popover, Tooltip } from '@cobliteam/ui/components';
import { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import { useLocalStorage } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import OnboardingChecklistContent from './OnboardingChecklistContent';
import OnboardingChecklistSkeleton from './OnboardingChecklistSkeleton';
import { type OnboardingSteps, OnboardingStepsList } from './OnboardingSteps';

const OnboardingChecklist = () => {
  const { t } = useTranslation('common');
  const { isAccessAllowed } = usePermissions();

  const { useGetUserActivationTasks } = useActivationTasks();
  const { data: userActivationTasks, isLoading, refetch } = useGetUserActivationTasks();

  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useLocalStorage<LocalUserPreferences>('localUserPreferences');

  const initialCheckedSteps = useMemo(() => {
    return userActivationTasks?.filter((task) => task.finished).map((task) => task.name) as OnboardingSteps[];
  }, [userActivationTasks]);

  const hideOnboardingBadge = useMemo(() => !preferences?.hideOnboardingBadge, [preferences?.hideOnboardingBadge]);

  const totalSteps = useMemo(() => {
    let count = 0;
    for (const category of OnboardingStepsList) {
      for (const step of category.steps) {
        if (isAccessAllowed(step.permissions as Permission[])) {
          count++;
        }
      }
    }
    return count;
  }, [isAccessAllowed]);

  const handleOpenOnboardingChecklist = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        refetch();
      } else {
        setPreferences((prevPrefs) => ({
          ...prevPrefs,
          hideOnboardingBadge: true,
        }));
      }

      return !prev;
    });

    Analytics.track('Onboarding Checklist - Sidebar button clicked');
  }, [refetch, setPreferences]);

  if (totalSteps === 0) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenOnboardingChecklist}>
      <Tooltip content={t('onboarding_checklist.tooltip')} asChild>
        <PopoverTrigger asChild>
          <div className='relative mt-1.5'>
            <Button
              data-testid='app-header-onboarding-checklist-button'
              className='text-default hover:text-purple-12'
              variant='surface'
              color='purple'
              size='sm'
              icon={<Icon name='book-marked' color='purple' />}
              aria-label='Ver checklist de onboarding'
            />
            {hideOnboardingBadge && (
              <span className='-top-0.5 -right-0.5 absolute flex size-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-accessory-purple-11 opacity-75' />
                <span className='relative inline-flex size-2 rounded-full bg-accessory-purple-11' />
              </span>
            )}
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align='end' side='right' showArrow={false} className='!px-0 flex max-h-[60vh] w-[32rem] flex-col overflow-hidden'>
        {isLoading ? <OnboardingChecklistSkeleton /> : <OnboardingChecklistContent initialCheckedSteps={initialCheckedSteps} onFinishOnboarding={() => setIsOpen(false)} />}
      </PopoverContent>
    </Popover>
  );
};

export default OnboardingChecklist;
